/**
 * CategorySection.tsx
 *
 * Renders category-tabbed media rail with tab filtering, incremental loading,
 * and category-aware analytics payload shaping.
 */

import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getSessionData } from "@repo/shared-utils/common-utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlaylistVideoLike } from "../../../types/livelanding.types";
import { getAllTranslation } from "../../../utils/lang";
import {
  getVideoElement,
  handleThumbnailClickPlayList,
  isLive as isVideoLive,
  safelyInvokeFireworkPlayer,
  timeSinceEnded,
} from "../../../utils/mediaUtils";
import {
  capitalizeFirstLetter,
  filterVideosByCategory,
  getCategoriesFromTabs,
  getCategoriesFromHashtags,
  hasVideosMatchingCategories,
  isAllCategory,
  normalizeCategoryName,
  type MediaData as MediaDataType,
} from "../../../utils/playlistCategoryUtils";
import { getCtSectionTitle } from "../../HeadlessPlaylistSection/headlessPlaylistUtils";
import { UnifiedMediaCard } from "../../MediaCard/UnifiedMediaCard";
import { Skeleton } from "../../SkeletonLoader/SkeletonLoader";
import { ALL_TAB_SERVER_KEY } from "../constants";
import { CategorySkeleton } from "../components/SectionSkeletons";
import { useFireworkSkeletonGate } from "../hooks/useFireworkSkeletonGate";
import { UnifiedMediaSectionProps } from "../types";

/**
 * File: CategorySection
 *
 * Handles `mode="category"` rendering with tabbed filtering,
 * incremental loading per active tab, and category-based CT events.
 */

/**
 * Adds category section CT fields for click/view events from category cards.
 */
function enrichCategoryVideoCT(
  base: Record<string, unknown>,
  params: {
    video: PlaylistVideoLike;
    title?: string;
    titleCt?: string;
    position?: number;
    eventTypeValue?: "click" | "view";
    customerId?: string;
  }
) {
  const { video, title, titleCt, position = 1, eventTypeValue = "click", customerId } = params;
  const ctSectionTitle = getCtSectionTitle(titleCt, title);
  const videoId = video?.trailer?.encoded_id || video?.encoded_id || video?.id || "NA";
  const categoryFromVideo =
    (typeof video?.event_description === "string" && video.event_description.trim()) ||
    (typeof video?.category === "string" && video.category.trim()) ||
    "NA";

  return {
    ...base,
    EP_COMPONENT_NAME: "CATEGORY_VIDEO",
    EP_REFERENCE_ID: "CATEGORY_VIDEO",
    EP_PROPERTY_TYPE: "CLP",
    EP_SECTION: ctSectionTitle || video?.caption || video?.event_name || "NA",
    ...(eventTypeValue === "click"
      ? {
          EP_CTA_TEXT: "PRODUCT_CLICK",
          EP_CTA: "PRODUCT_CLICK",
          EP_SECTION_CTA: "KNOW_MORE",
        }
      : {}),
    EP_PRODUCT_NAME: categoryFromVideo,
    EP_PRODUCT_CATEGORY: `${categoryFromVideo || "NA"} || ${videoId || "NA"}`,
    EP_PAGE_NAME: dlElementConstants.LIVE_COMMERCE_CLP,
    EP_PAGE_URL: typeof window !== "undefined" ? window.location.href : "NA",
    EP_EVENT_TYPE: eventTypeValue.toUpperCase(),
    EP_VIDEO_TYPE: "VIDEO_POP_UP",
    EP_JOURNEY: "LIVE_COMMERCE",
    EP_SEGMENT: "LIVE_COMMERCE",
    EP_ID: videoId,
    EP_LIVEVIDEO_IMPRESSION: videoId,
    EP_VIDEO_TITLE: video?.caption || video?.title || video?.event_name || "NA",
    EP_STATUS: video?.live_stream_status || "NA",
    EP_LIVE_SESSION: video?.live_stream_status === "active" ? "ACTIVE" : "INACTIVE",
    EP_POSITION: position.toString(),
    EP_SR_NUMBER: position.toString(),
    EP_CUSTOMER_TYPE: "NTB",
    EP_SPRINT: "DPAOP3",
    EP_DESTINATION: "NA",
    EP_SUBSECTION: "NA",
    EP_NUDGE: "NA",
    EP_SEARCH: "NA",
    EP_CUSTOMER_ID: customerId || "NA",
  };
}

export function CategorySection({
  title,
  titleCt,
  sectionId = "",
  language = "en",
  playListId,
  tabs,
  initialMediaData,
  initialMediaDataByTab,
  initialPaginationByTab,
  tabsApiBaseUrl,
  reduceMobileSectionHeadingSize = false,
}: Readonly<
  Pick<
    UnifiedMediaSectionProps,
    | "title"
    | "titleCt"
    | "sectionId"
    | "language"
    | "playListId"
    | "tabs"
    | "initialMediaData"
    | "initialMediaDataByTab"
    | "initialPaginationByTab"
    | "tabsApiBaseUrl"
    | "reduceMobileSectionHeadingSize"
  >
>) {
  const ctSectionTitle = getCtSectionTitle(titleCt, title);

  const headingSizeClassName = reduceMobileSectionHeadingSize
    ? "text-[16px] md:text-[20px]"
    : "text-[20px] sm:text-[16px] md:text-[20px]";
  const effectiveTabsApiBaseUrl = (tabsApiBaseUrl || "").trim();
  const hasInitialData = initialMediaData !== null && initialMediaData !== undefined;

  const resolvedTabsEndpoint = useMemo(() => {
    if (!effectiveTabsApiBaseUrl) return "";
    const trimmed = effectiveTabsApiBaseUrl.replace(/\/$/, "");
    return trimmed.includes("/api/v1/live-streams/videos/by-playlist")
      ? trimmed
      : `${trimmed}/api/v1/live-streams/videos/by-playlist`;
  }, [effectiveTabsApiBaseUrl]);

  const [customerId, setCustomerId] = useState("NA");
  const [allMediaData, setAllMediaData] = useState<MediaDataType | null>(initialMediaData ?? null);
  const [mediaDataByTab, setMediaDataByTab] = useState<Record<string, MediaDataType | null>>(
    initialMediaDataByTab ?? { [ALL_TAB_SERVER_KEY]: initialMediaData ?? null }
  );
  const [paginationByTab, setPaginationByTab] = useState<
    Record<string, { page: number; limit: number; total: number } | null>
  >(initialPaginationByTab ?? {});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const [activeCategory, setActiveCategory] = useState(getAllTranslation(language));

  const resolveCategoryTabKey = useCallback(
    (category: string): string => {
      if (isAllCategory(category, language)) return ALL_TAB_SERVER_KEY;

      const normalizedCategory = normalizeCategoryName(category, language).toLowerCase();

      const candidates = [
        ...Object.keys(mediaDataByTab ?? {}),
        ...Object.keys(initialMediaDataByTab ?? {}),
        ...(tabs ?? []).flatMap((group) =>
          Object.values(group).map((tab) => tab?.tabName?.trim() || "")
        ),
      ];

      const matched = candidates.find(
        (candidate) =>
          !!candidate &&
          normalizeCategoryName(candidate, language).toLowerCase() === normalizedCategory
      );

      return matched || category;
    },
    [initialMediaDataByTab, language, mediaDataByTab, tabs]
  );

  useEffect(() => {
    let isMounted = true;

    getSessionData()
      .then((session) => {
        if (!isMounted) return;
        setCustomerId(session?.custId || "NA");
      })
      .catch(() => {
        if (!isMounted) return;
        setCustomerId("NA");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!initialMediaDataByTab) return;
    setMediaDataByTab(initialMediaDataByTab);
  }, [initialMediaDataByTab]);

  useEffect(() => {
    if (!initialPaginationByTab) return;
    setPaginationByTab(initialPaginationByTab);
  }, [initialPaginationByTab]);

  const mergeVideos = useCallback(
    (prev: MediaDataType | null, incoming: MediaDataType | null): MediaDataType | null => {
      if (!incoming) return prev;
      if (!prev) return incoming;

      const prevStreams = Array.isArray(prev.live_streams) ? prev.live_streams : [];
      const incomingStreams = Array.isArray(incoming.live_streams) ? incoming.live_streams : [];
      const seen = new Set<string>();
      const merged = [...prevStreams, ...incomingStreams].filter((video) => {
        const key =
          String(video?.id ?? "") ||
          String(video?.encoded_id ?? "") ||
          String(video?.trailer?.encoded_id ?? "");
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return {
        ...prev,
        live_streams: merged,
      };
    },
    []
  );

  const extractMediaFromResponse = useCallback((json: unknown): MediaDataType => {
    const payload = json as {
      data?: {
        upcomingliveFromFireworks?: { live_streams?: unknown };
        videos?: unknown;
      };
      upcomingliveFromFireworks?: { live_streams?: unknown };
      videos?: unknown;
      live_streams?: unknown;
    };
    const streams =
      payload?.data?.upcomingliveFromFireworks?.live_streams ??
      payload?.data?.videos ??
      payload?.upcomingliveFromFireworks?.live_streams ??
      payload?.videos ??
      payload?.live_streams ??
      [];

    return {
      live_streams: Array.isArray(streams) ? streams : [],
    } as MediaDataType;
  }, []);

  const extractPaginationFromResponse = useCallback(
    (json: unknown, fallback: { page: number; limit: number; total: number }) => {
      const payload = json as {
        data?: {
          page?: unknown;
          limit?: unknown;
          total?: unknown;
          paging?: { total?: unknown };
          total_videos?: unknown;
        };
        page?: unknown;
        limit?: unknown;
        total?: unknown;
        paging?: { total?: unknown };
        total_videos?: unknown;
      };
      const page = Number(payload?.data?.page ?? payload?.page);
      const limit = Number(payload?.data?.limit ?? payload?.limit);
      const total = Number(
        payload?.data?.total ??
          payload?.total ??
          payload?.data?.paging?.total ??
          payload?.paging?.total ??
          payload?.data?.total_videos ??
          payload?.total_videos
      );

      return {
        page: Number.isFinite(page) && page > 0 ? page : fallback.page,
        limit: Number.isFinite(limit) && limit > 0 ? limit : fallback.limit,
        total: Number.isFinite(total) && total >= 0 ? total : fallback.total,
      };
    },
    []
  );

  const loadMoreForActiveTab = useCallback(async () => {
    if (isLoadingMoreRef.current || isLoadingMore) return;
    if (!effectiveTabsApiBaseUrl) return;

    const tabKey = resolveCategoryTabKey(activeCategory);
    const currentData = mediaDataByTab[tabKey] ?? null;
    const currentCount = currentData?.live_streams?.length ?? 0;
    const currentPagination = paginationByTab[tabKey];

    if (!currentPagination) return;
    if (currentPagination.total > 0 && currentCount >= currentPagination.total) return;

    const nextPage = currentPagination.page + 1;
    const limit = currentPagination.limit || 10;

    const playlistIdForTab = playListId || "";

    const categoryParam =
      isAllCategory(activeCategory, language) || tabKey === ALL_TAB_SERVER_KEY ? "" : tabKey;

    const endpoint = `${resolvedTabsEndpoint}?playlistId=${encodeURIComponent(playlistIdForTab)}&video_description=${encodeURIComponent(categoryParam)}&limit=${encodeURIComponent(String(limit))}&page=${encodeURIComponent(String(nextPage))}`;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return;

      const json = await response.json();
      const incomingData = extractMediaFromResponse(json);
      const mergedData = mergeVideos(currentData, incomingData);
      const mergedCount = mergedData?.live_streams?.length ?? 0;
      const noNewUniqueVideos = mergedCount <= currentCount;
      const nextPagination = extractPaginationFromResponse(json, {
        page: nextPage,
        limit,
        total: currentPagination.total,
      });

      const nextTotalFromApi =
        nextPagination.total > 0 ? nextPagination.total : currentPagination.total;
      const nextTotal = noNewUniqueVideos ? mergedCount : nextTotalFromApi;

      const resolvedPagination = {
        ...nextPagination,
        total: nextTotal,
      };

      setMediaDataByTab((prev) => ({
        ...prev,
        [tabKey]: mergedData,
      }));

      setPaginationByTab((prev) => ({
        ...prev,
        [tabKey]: resolvedPagination,
      }));

      setAllMediaData((prev) => mergeVideos(prev, incomingData));
    } catch {
      // no-op
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [
    activeCategory,
    effectiveTabsApiBaseUrl,
    resolvedTabsEndpoint,
    extractMediaFromResponse,
    extractPaginationFromResponse,
    isLoadingMore,
    language,
    mediaDataByTab,
    mergeVideos,
    paginationByTab,
    playListId,
    resolveCategoryTabKey,
  ]);

  const hasMoreForActiveTab = useMemo(() => {
    const tabKey = resolveCategoryTabKey(activeCategory);
    const currentCount = mediaDataByTab[tabKey]?.live_streams?.length ?? 0;
    const currentPagination = paginationByTab[tabKey];

    if (!currentPagination) return false;
    if (currentPagination.total > 0) return currentCount < currentPagination.total;
    return true;
  }, [activeCategory, mediaDataByTab, paginationByTab, resolveCategoryTabKey]);

  const availableCategories = useMemo(() => {
    if (tabs && Array.isArray(tabs) && tabs.length > 0) {
      const categoriesFromData = getCategoriesFromTabs(tabs, language);
      if (categoriesFromData.length > 0) {
        return categoriesFromData;
      }

      const configuredTabs = tabs
        .flatMap((group) => Object.values(group).map((tab) => tab?.tabName?.trim() || ""))
        .filter((value) => value && !isAllCategory(value, language));

      const uniqueConfiguredTabs: string[] = [];
      const seenConfiguredTabs = new Set<string>();
      for (const tabName of configuredTabs) {
        const normalized = normalizeCategoryName(tabName, language).toLowerCase();
        if (!normalized || seenConfiguredTabs.has(normalized)) continue;
        seenConfiguredTabs.add(normalized);
        uniqueConfiguredTabs.push(tabName);
      }

      return uniqueConfiguredTabs.length > 0
        ? [getAllTranslation(language), ...uniqueConfiguredTabs]
        : [];
    }
    return getCategoriesFromHashtags(allMediaData, language);
  }, [allMediaData, language, tabs]);

  const allCategoryDisplayName = useMemo(() => {
    const allCat = availableCategories.find((cat) => isAllCategory(cat, language));
    return allCat || getAllTranslation(language);
  }, [availableCategories, language]);

  const firedVideoIdsRef = useRef<Set<string>>(new Set());

  const hasValidData = useMemo(
    () => Boolean(playListId) || (hasInitialData && (allMediaData?.live_streams?.length ?? 0) > 0),
    [allMediaData, hasInitialData, playListId]
  );

  const hasMatchingVideos = useMemo(
    () => hasVideosMatchingCategories(allMediaData, availableCategories, language),
    [allMediaData, availableCategories, language]
  );

  const hasAnyVideosAcrossTabs = useMemo(() => {
    const tabHasVideos = Object.values(mediaDataByTab).some(
      (dataset) => (dataset?.live_streams?.length ?? 0) > 0
    );
    const currentHasVideos = (allMediaData?.live_streams?.length ?? 0) > 0;
    return tabHasVideos || currentHasVideos;
  }, [allMediaData, mediaDataByTab]);

  const filteredVideos = useMemo(() => {
    if (!allMediaData?.live_streams) return [];

    const normalizedCategory = normalizeCategoryName(activeCategory, language);
    const categoriesToFilterBy = isAllCategory(activeCategory, language)
      ? undefined
      : availableCategories;

    return filterVideosByCategory(
      allMediaData.live_streams,
      normalizedCategory,
      language,
      categoriesToFilterBy
    );
  }, [activeCategory, allMediaData, availableCategories, language]);

  const findVideoByEncodedId = useCallback(
    (videoEncodedId: string): PlaylistVideoLike | undefined =>
      filteredVideos.find(
        (video) =>
          video.trailer?.encoded_id === videoEncodedId || video.encoded_id === videoEncodedId
      ) as PlaylistVideoLike | undefined,
    [filteredVideos]
  );

  useEffect(() => {
    const handleFWPlayerReady = () => {
      try {
        const fwWindow = window as Window & { _fwn?: { players?: Record<string, unknown> } };
        const players = fwWindow._fwn?.players || {};
        const firstPlayer = Object.values(players)[0] as
          | {
              video?: { encoded_id?: string };
              currentVideo?: { encoded_id?: string };
            }
          | undefined;
        if (!firstPlayer) return;

        const videoId = firstPlayer?.video?.encoded_id || firstPlayer?.currentVideo?.encoded_id;
        if (!videoId) return;

        if (firedVideoIdsRef.current.has(videoId)) return;
        firedVideoIdsRef.current.add(videoId);

        const video = findVideoByEncodedId(videoId);
        if (!video) return;

        dataLayer(
          "",
          {},
          enrichCategoryVideoCT(
            { EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED },
            {
              video,
              title,
              titleCt,
              eventTypeValue: "click",
              customerId,
            }
          )
        );
      } catch {
        // analytics should not break UX
      }
    };

    document.addEventListener("fw:player:ready", handleFWPlayerReady);
    return () => {
      document.removeEventListener("fw:player:ready", handleFWPlayerReady);
    };
  }, [customerId, findVideoByEncodedId, title, titleCt]);

  const shouldShowSection = useMemo(
    () =>
      hasValidData &&
      availableCategories.length > 0 &&
      (hasAnyVideosAcrossTabs || (Array.isArray(tabs) && tabs.length > 0)),
    [availableCategories.length, hasAnyVideosAcrossTabs, hasValidData, tabs]
  );

  const categoryLoading = !useFireworkSkeletonGate(filteredVideos.length > 0, 300);

  useEffect(() => {
    const normalizedActive = normalizeCategoryName(activeCategory, language);
    const normalizedAvailable = availableCategories.map((cat) =>
      normalizeCategoryName(cat, language)
    );

    if (
      !normalizedAvailable.includes(normalizedActive) ||
      (isAllCategory(activeCategory, language) && allCategoryDisplayName !== activeCategory)
    ) {
      setActiveCategory(allCategoryDisplayName);
    }
  }, [activeCategory, allCategoryDisplayName, availableCategories, language]);

  const handleThumbnailClick = useCallback(
    (videoEncodedId: string) => {
      handleThumbnailClickPlayList({
        videoEncodedId,
        title: ctSectionTitle,
        sectionId,
        findVideoByEncodedId,
        getVideoElement,
        safelyInvokeFireworkPlayer,
      });
    },
    [ctSectionTitle, findVideoByEncodedId, sectionId]
  );

  const handleCategoryClick = useCallback(
    (category: string) => {
      setActiveCategory(category);

      if (initialMediaDataByTab) {
        const tabKey = resolveCategoryTabKey(category);
        const nextData = isAllCategory(category, language)
          ? (mediaDataByTab[ALL_TAB_SERVER_KEY] ??
            initialMediaDataByTab[ALL_TAB_SERVER_KEY] ??
            initialMediaData ??
            null)
          : (mediaDataByTab[tabKey] ?? initialMediaDataByTab[tabKey] ?? null);

        if (nextData) {
          setAllMediaData(nextData);
        }
      }

      dataLayer(
        "",
        {
          section_title: ctSectionTitle,
          component: componentNameConstants.VIDEO_GRID,
          event: dlEventConstants.TAB_CLICK,
          element: dlEventConstants.TAB_CLICK,
          cta_text: category,
        },
        {
          EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
          EP_PAGE_NAME: dlElementConstants.LIVE_COMMERCE_CLP,
          EP_JOURNEY_NAME: dlElementConstants.LIVE_COMMERCE,
          EP_SECTION: ctSectionTitle,
          EP_CTA: "TAB_CLICK",
          EP_CTA_TEXT: category,
        }
      );
    },
    [
      ctSectionTitle,
      initialMediaData,
      initialMediaDataByTab,
      language,
      mediaDataByTab,
      resolveCategoryTabKey,
    ]
  );

  const validCategories = useMemo(() => availableCategories.filter(Boolean), [availableCategories]);

  const renderTabSection = useMemo(() => {
    if (categoryLoading || validCategories.length === 0) return null;

    return (
      <div
        className="mb-[10px] w-full [scrollbar-width:none] overflow-x-auto border-b border-gray-200 transition-all duration-300 ease-in-out [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={`${title} categories`}
        aria-orientation="horizontal"
        aria-live="polite"
      >
        <div className="flex gap-0">
          {validCategories.map((cat) => {
            const isActive =
              normalizeCategoryName(activeCategory, language) ===
              normalizeCategoryName(cat, language);

            return (
              <button
                key={cat}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${cat}`}
                id={`tab-${cat}`}
                tabIndex={isActive ? 0 : -1}
                className={`mr-[10px] cursor-pointer py-[8px] !text-[12px] whitespace-nowrap transition-all duration-300 ease-in-out sm:!text-[16px] ${
                  isActive
                    ? "rounded-tl-[10px] rounded-tr-[10px] border-b-2 border-[#FF6700] font-medium text-[#FF6700]"
                    : "border-b-2 border-transparent font-normal text-[#002953] hover:text-[#FF6700]"
                }`}
                onClick={() => handleCategoryClick(cat)}
              >
                {capitalizeFirstLetter(cat)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [activeCategory, categoryLoading, handleCategoryClick, language, title, validCategories]);

  const renderListSection = useMemo(() => {
    if (categoryLoading) return null;

    if (!hasMatchingVideos || filteredVideos.length === 0) {
      return (
        <output className="mt-4 block text-center text-gray-500" aria-live="polite">
          No {capitalizeFirstLetter(activeCategory)} videos found
        </output>
      );
    }

    return (
      <div
        id={`panel-${activeCategory}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeCategory}`}
        aria-label={`${title} video list`}
        className="flex items-start gap-[7px] overflow-x-auto scroll-smooth transition-opacity duration-500 ease-in-out sm:gap-[6px] md:gap-[8px] [&::-webkit-scrollbar]:hidden"
        onScroll={(event) => {
          const element = event.currentTarget;
          const threshold = 80;
          const remaining = element.scrollWidth - element.scrollLeft - element.clientWidth;
          if (remaining <= threshold && hasMoreForActiveTab && !isLoadingMoreRef.current) {
            loadMoreForActiveTab();
          }
        }}
      >
        {filteredVideos.map((video, index) => {
          const key = video.id ?? video.encoded_id ?? video.trailer?.encoded_id ?? index;
          return (
            <UnifiedMediaCard
              key={key}
              variant="web"
              video={video}
              onThumbnailClick={handleThumbnailClick}
              isLive={isVideoLive(video.started_at, video.ended_at)}
              timeSinceEndedWeb={timeSinceEnded}
              sectionId={sectionId}
              isAllTab={isAllCategory(activeCategory, language)}
              activeCategory={activeCategory}
              language={language}
            />
          );
        })}
        {isLoadingMore && (
          <div className="min-w-[110px] shrink-0 sm:min-w-[140px] md:min-w-[190px] lg:min-w-[200px] xl:min-w-[210px]">
            <Skeleton
              variant="rectangular"
              className="mb-3 aspect-[9/15.8] w-full rounded-lg bg-gray-200 sm:aspect-[9/16.3]"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
    );
  }, [
    activeCategory,
    categoryLoading,
    filteredVideos,
    handleThumbnailClick,
    hasMatchingVideos,
    hasMoreForActiveTab,
    isLoadingMore,
    language,
    loadMoreForActiveTab,
    sectionId,
    title,
  ]);

  if (!shouldShowSection && !categoryLoading) {
    return null;
  }

  return (
    <section
      className="border-b-2 border-gray-200 bg-white px-5 py-2 sm:px-2.5 sm:py-2 md:px-5 md:py-2"
      aria-labelledby={sectionId}
      aria-label={`${title} section`}
    >
      <div className="mb-5">
        <h2
          id={sectionId}
          className={`-mb-4 font-medium text-[#1A1A1A] xl:text-[20px] ${headingSizeClassName}`}
        >
          {title}
        </h2>
      </div>

      {categoryLoading && <CategorySkeleton title={title} />}

      {renderTabSection}

      {renderListSection}
    </section>
  );
}
